import React, { useState, useEffect } from 'react';
import { X, GitBranch, ArrowRight, CheckCircle, AlertTriangle, XCircle, RefreshCw, Check, FileText, Eye, Download } from 'lucide-react';
import api from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReconciliationItem {
  id: number;
  itinerary_id: number;
  name: string;
  item_type: string;
  item_subtype: string;
  activity_id?: number;
  day_number: number;
  activity_date: string | null;
  hotel_name: string | null;
  room_type: string | null;
  checkin_date: string | null;
  checkout_date: string | null;
  no_of_nights: number | null;
  no_of_rooms: number | null;
  calculated_adults?: number;
  calculated_kids?: number;
  estimated_amount: number;
  actual_amount: number;
  vendor_id: number | null;
  vendor_name: string | null;
  status: string;
  booking_reference: string | null;
  invoice_number: string | null;
  payment_date: string | null;
  remarks: string | null;
  source_version?: number;
  source_wyn_id?: string;
}

interface MatchedPair {
  prev: ReconciliationItem;
  current: ReconciliationItem;
  differences: string[];
  has_differences: boolean;
}

interface ReconciliationData {
  needs_reconciliation: boolean;
  cancellation_mode?: boolean;
  items_to_cancel?: ReconciliationItem[];
  quotation_number: string;
  current_version: { itinerary_id: number; version_number: number; wyn_id: string };
  previous_version: { itinerary_id: number; version_number: number; wyn_id: string };
  summary: {
    matched: number;
    matched_with_changes: number;
    prev_only: number;
    current_only: number;
    needs_attention: number;
  };
  matched: MatchedPair[];
  prev_only: ReconciliationItem[];
  current_only: ReconciliationItem[];
  prev_vendor_files: any[];
}

interface Props {
  itineraryId: number;
  onClose: () => void;
}

type Tab = 'accommodation' | 'prebooked' | 'driver_paid' | 'transportation';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB'); } catch { return d; }
};

const fmtAmt = (n: number) => n ? n.toLocaleString() : '0';

const STATUS_COLORS: Record<string, string> = {
  NOT_BOOKED: 'bg-gray-100 text-gray-600',
  BOOKED: 'bg-blue-100 text-blue-700',
  PENDING_PAYMENT: 'bg-yellow-100 text-yellow-700',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-700',
  PAYMENT_COMPLETED: 'bg-green-100 text-green-700',
  CANCELED_NO_FEE: 'bg-gray-200 text-gray-500',
  CANCELED_WITH_FEE: 'bg-red-100 text-red-700',
};

/** Derive human-readable action items from a diff list + item context */
function getActionItems(pair: MatchedPair): string[] {
  const actions: string[] = [];
  const { prev, current, differences } = pair;

  if (current.status === 'NOT_BOOKED' && prev.status !== 'NOT_BOOKED') {
    actions.push(`Re-book with ${prev.vendor_name || 'vendor'} — was ${prev.status}`);
  }

  for (const d of differences) {
    if (d.startsWith('Check-in') || d.startsWith('Check-out')) {
      actions.push(`Update hotel dates — ${d}`);
    } else if (d.startsWith('Date')) {
      actions.push(`Update activity date — ${d}`);
    } else if (d.startsWith('Day')) {
      actions.push(`Confirm day change with vendor — ${d}`);
    } else if (d.startsWith('Amount')) {
      actions.push(`Update invoice amount — ${d}`);
    } else if (d.startsWith('Type')) {
      actions.push(`Item type changed — verify booking category — ${d}`);
    }
  }

  if (prev.vendor_name && current.status === 'NOT_BOOKED') {
    actions.push(`Assign vendor: ${prev.vendor_name}`);
  }

  if (prev.booking_reference && !current.booking_reference) {
    actions.push(`Previous ref: ${prev.booking_reference} — confirm if still valid`);
  }

  return actions;
}

/** Filter matched/prev_only/current_only by tab */
function filterByTab(data: ReconciliationData, tab: Tab) {
  const typeMap: Record<Tab, string[]> = {
    accommodation: ['accommodation'],
    prebooked: ['prebooked'],
    driver_paid: ['driver_paid'],
    transportation: ['transportation'],
  };
  const types = typeMap[tab];
  const match = (item: ReconciliationItem) =>
    types.some(t => item.item_type?.toLowerCase().includes(t));

  return {
    matched: data.matched.filter(m => match(m.current) || match(m.prev)),
    prev_only: data.prev_only.filter(match),
    current_only: data.current_only.filter(match),
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const ItemDetail: React.FC<{ item: ReconciliationItem; side: 'prev' | 'current'; label: string }> = ({ item, side, label }) => (
  <div className={`rounded-lg border p-2.5 text-xs space-y-0.5 ${side === 'prev' ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'}`}>
    <div className={`text-[10px] font-semibold mb-1 ${side === 'prev' ? 'text-red-700' : 'text-blue-700'}`}>{label}</div>
    <div className="font-semibold text-gray-900 truncate">{item.name}</div>
    <div className="text-gray-500">Day {item.day_number || '—'} · {item.item_type}</div>
    {item.activity_date && <div>Date: <strong>{fmtDate(item.activity_date)}</strong></div>}
    {item.hotel_name && <div>Hotel: <strong>{item.hotel_name}</strong></div>}
    {item.checkin_date && (
      <div>Check-in: <strong>{fmtDate(item.checkin_date)}</strong> → <strong>{fmtDate(item.checkout_date)}</strong></div>
    )}
    {item.no_of_nights != null && <div>Nights: <strong>{item.no_of_nights}</strong> · Rooms: <strong>{item.no_of_rooms}</strong></div>}
    <div>Est: <strong>{fmtAmt(item.estimated_amount)}</strong>{item.actual_amount > 0 ? ` · Act: ${fmtAmt(item.actual_amount)}` : ''}</div>
    {item.vendor_name && <div className="text-indigo-600 font-medium">Vendor: {item.vendor_name}</div>}
    {item.booking_reference && <div>Ref: <strong>{item.booking_reference}</strong></div>}
    {item.payment_date && <div>Paid: <strong>{fmtDate(item.payment_date)}</strong></div>}
    <span className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600'}`}>
      {item.status}
    </span>
  </div>
);

const VendorFilesRow: React.FC<{ vendorName: string; prevItineraryId: number; currentItineraryId: number }> = ({ vendorName, prevItineraryId, currentItineraryId }) => {
  const [files, setFiles] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [opening, setOpening] = useState<string | null>(null);

  useEffect(() => {
    if (!vendorName) return;
    setLoaded(false);
    const sections = ['prebooked_activities', 'accommodation'];
    const itinIds = Array.from(new Set([prevItineraryId, currentItineraryId]));
    const promises = itinIds.flatMap(iid =>
      sections.map(sec =>
        api.get(`/operational-cost/${iid}/files/${sec}?vendor=${encodeURIComponent(vendorName)}`)
          .then(res => (res.data.success ? (res.data.data || []).map((f: any) => ({ ...f, itinerary_id: iid })) : []))
          .catch(() => [])
      )
    );
    Promise.all(promises).then(results => {
      const all = results.flat();
      const seen = new Set<string>();
      const unique = all.filter(f => { if (seen.has(f.key)) return false; seen.add(f.key); return true; });
      setFiles(unique);
      setLoaded(true);
    });
  }, [vendorName, prevItineraryId, currentItineraryId]);

  const openFile = async (s3Key: string, fileName: string, forDownload = false) => {
    try {
      setOpening(s3Key);
      const endpoint = forDownload ? `/files/download-url/${s3Key}` : `/files/presigned-url/${s3Key}`;
      const res = await api.get(endpoint);
      const url = res.data?.data?.url || res.data?.download_url;
      if (url) {
        const a = document.createElement('a');
        a.href = url;
        if (forDownload) { a.download = fileName; }
        else { a.target = '_blank'; a.rel = 'noopener noreferrer'; }
        a.click();
      }
    } catch {
      window.alert('Could not open file. Please try again.');
    } finally {
      setOpening(null);
    }
  };

  if (!vendorName) return null;

  return (
    <div className="mt-1.5 p-2 bg-gray-50 border border-gray-200 rounded text-xs">
      <div className="flex items-center gap-1 text-gray-600 font-medium mb-1">
        <FileText className="w-3 h-3" /> {vendorName} files
        {!loaded && <RefreshCw className="w-3 h-3 animate-spin ml-1" />}
      </div>
      {loaded && files.length === 0 ? (
        <span className="text-gray-400 italic">No files uploaded</span>
      ) : (
        <div className="space-y-1">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 text-gray-700 bg-white border rounded px-2 py-1">
              <span className="text-[10px] px-1 py-0.5 bg-gray-100 border rounded shrink-0">{f.source === 'receipt' ? 'receipt' : 'invoice'}</span>
              <span className="truncate flex-1 text-[11px]">{f.name}</span>
              <span className="text-gray-400 text-[10px] shrink-0">WH_{f.itinerary_id}</span>
              <button
                onClick={() => openFile(f.key, f.name, false)}
                disabled={opening === f.key}
                className="p-1 text-blue-600 hover:bg-blue-50 rounded shrink-0 disabled:opacity-50"
                title="View"
              >
                {opening === f.key ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
              </button>
              <button
                onClick={() => openFile(f.key, f.name, true)}
                disabled={opening === f.key}
                className="p-1 text-green-600 hover:bg-green-50 rounded shrink-0 disabled:opacity-50"
                title="Download"
              >
                <Download className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const BookingReconciliationPanelV2: React.FC<Props> = ({ itineraryId, onClose }) => {
  const [data, setData] = useState<ReconciliationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('accommodation');
  const [carrying, setCarrying] = useState<Set<number>>(new Set());
  const [carryingAll, setCarryingAll] = useState(false);
  const [reviewed, setReviewed] = useState<Set<number>>(new Set());
  const [reviewing, setReviewing] = useState<Set<number>>(new Set());

  useEffect(() => { fetchData(); }, [itineraryId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/booking-reconciliation/${itineraryId}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const carryForward = async (prevId: number, currentId: number) => {
    try {
      setCarrying(s => new Set(s).add(currentId));
      await api.post(`/booking-reconciliation/${itineraryId}/carry-forward`, {
        prev_item_id: prevId, current_item_id: currentId
      });
      await fetchData();
    } catch (err: any) {
      window.alert(err.response?.data?.detail || 'Failed to carry forward');
    } finally {
      setCarrying(s => { const n = new Set(s); n.delete(currentId); return n; });
    }
  };

  const carryForwardAll = async () => {
    if (!data) return;
    const matches = data.matched
      .filter(m => m.current.status === 'NOT_BOOKED' && m.prev.status !== 'NOT_BOOKED')
      .map(m => ({ prev_item_id: m.prev.id, current_item_id: m.current.id }));
    if (!matches.length) { window.alert('No NOT_BOOKED items to carry forward'); return; }
    if (!window.confirm(`Carry forward ${matches.length} items?`)) return;
    try {
      setCarryingAll(true);
      await api.post(`/booking-reconciliation/${itineraryId}/carry-forward-all`, { matches });
      await fetchData();
    } catch (err: any) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally {
      setCarryingAll(false);
    }
  };

  const markReviewed = async (itemId: number) => {
    try {
      setReviewing(s => new Set(s).add(itemId));
      await api.post(`/booking-reconciliation/${itineraryId}/mark-reviewed`, { item_id: itemId });
      setReviewed(s => new Set(s).add(itemId));
    } catch (err: any) {
      window.alert(err.response?.data?.detail || 'Failed to mark reviewed');
    } finally {
      setReviewing(s => { const n = new Set(s); n.delete(itemId); return n; });
    }
  };

  // ── Loading / empty states ──────────────────────────────────────────────────

  if (loading) return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 text-center">
        <RefreshCw className="animate-spin h-8 w-8 text-blue-600 mx-auto" />
        <p className="mt-3 text-gray-600">Loading reconciliation...</p>
      </div>
    </div>
  );

  if (!data || !data.needs_reconciliation) return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-6 text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold mb-2">No Reconciliation Needed</h3>
        <p className="text-gray-500 text-sm mb-4">No previous versions with bookings to compare.</p>
        <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm">Close</button>
      </div>
    </div>
  );

  // ── Tab counts ──────────────────────────────────────────────────────────────

  const tabCount = (tab: Tab) => {
    if (data.cancellation_mode && data.items_to_cancel) {
      const typeMap: Record<Tab, string[]> = {
        accommodation: ['accommodation'],
        prebooked: ['prebooked'],
        driver_paid: ['driver_paid'],
        transportation: ['transportation'],
      };
      return data.items_to_cancel.filter(item =>
        typeMap[tab].some(t => item.item_type?.toLowerCase().includes(t))
      ).length;
    }
    const f = filterByTab(data, tab);
    return f.matched.length + f.prev_only.length + f.current_only.length;
  };

  const TABS: { key: Tab; label: string; emoji: string }[] = [
    { key: 'accommodation', label: 'Accommodations', emoji: '🏨' },
    { key: 'prebooked', label: 'Prebooked', emoji: '🎫' },
    { key: 'driver_paid', label: 'Driver Paid', emoji: '💰' },
    { key: 'transportation', label: 'Transportation', emoji: '🚗' },
  ];

  const { matched, prev_only, current_only } = filterByTab(data, activeTab);
  const { summary } = data;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl w-full max-w-5xl h-[95vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-xl flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-indigo-600" />
              <h2 className="text-base font-bold text-gray-900">Booking Reconciliation</h2>
              {data.quotation_number && (
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">{data.quotation_number}</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              {data.cancellation_mode
                ? <span className="text-red-600 font-medium">Active bookings to cancel across all versions</span>
                : <>{data.previous_version.wyn_id} (v{data.previous_version.version_number})
                    <ArrowRight className="w-3 h-3 inline mx-1" />
                    {data.current_version.wyn_id} (v{data.current_version.version_number})
                  </>
              }
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Summary bar */}
        <div className="grid grid-cols-4 gap-2 px-4 py-2.5 border-b flex-shrink-0 bg-white">
          {[
            { label: 'Matched', val: summary.matched, color: 'text-green-700 bg-green-50' },
            { label: 'Changed', val: summary.matched_with_changes, color: 'text-amber-700 bg-amber-50' },
            { label: 'Need Cancel', val: data.cancellation_mode ? (data.items_to_cancel?.length || 0) : summary.prev_only, color: 'text-red-700 bg-red-50' },
            { label: 'Need Booking', val: summary.current_only, color: 'text-blue-700 bg-blue-50' },
          ].map(s => (
            <div key={s.label} className={`rounded-lg p-2 text-center ${s.color}`}>
              <div className="text-lg font-bold">{s.val}</div>
              <div className="text-[10px]">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b flex-shrink-0">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex-1 py-2.5 text-xs font-medium text-center transition-colors ${
                activeTab === t.key
                  ? 'border-b-2 border-indigo-500 text-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.emoji} {t.label}
              {tabCount(t.key) > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px]">
                  {tabCount(t.key)}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">

          {/* Cancellation mode banner */}
          {data.cancellation_mode && data.items_to_cancel && (() => {
            const typeMap: Record<Tab, string[]> = {
              accommodation: ['accommodation'],
              prebooked: ['prebooked'],
              driver_paid: ['driver_paid'],
              transportation: ['transportation'],
            };
            const tabTypes = typeMap[activeTab];
            const tabItems = data.items_to_cancel.filter(item =>
              tabTypes.some(t => item.item_type?.toLowerCase().includes(t))
            );
            if (tabItems.length === 0) return (
              <div className="text-center py-12 text-gray-400">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No items in this category</p>
              </div>
            );
            return (
            <div className="border-2 border-red-400 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 bg-red-600 text-white">
                <XCircle className="w-5 h-5" />
                <div>
                  <div className="font-bold text-sm">⚠️ ITINERARY CANCELED — Vendor Bookings Must Be Cancelled</div>
                  <div className="text-xs text-red-100 mt-0.5">
                    {tabItems.length} booking{tabItems.length !== 1 ? 's' : ''} in this category need to be cancelled
                  </div>
                </div>
              </div>
              <div className="p-3 space-y-2 bg-red-50">
                {tabItems.map((item, i) => (
                  <div key={i} className="bg-white border border-red-200 rounded-lg p-3 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900">{item.name}</span>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-600'}`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="text-gray-500 space-y-0.5">
                      <div>Day {item.day_number} · {item.item_type}</div>
                      {item.hotel_name && <div>Hotel: <strong>{item.hotel_name}</strong> · {fmtDate(item.checkin_date)} → {fmtDate(item.checkout_date)}</div>}
                      {item.activity_date && <div>Date: <strong>{fmtDate(item.activity_date)}</strong></div>}
                      {item.vendor_name && <div className="text-indigo-600 font-medium">Vendor: {item.vendor_name}</div>}
                      {item.booking_reference && <div>Ref: <strong>{item.booking_reference}</strong></div>}
                      <div>Est: <strong>{fmtAmt(item.estimated_amount)}</strong>{item.actual_amount > 0 ? ` · Act: ${fmtAmt(item.actual_amount)}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            );
          })()}

          {matched.length === 0 && prev_only.length === 0 && current_only.length === 0 && !data.cancellation_mode && (
            <div className="text-center py-12 text-gray-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No items in this category</p>
            </div>
          )}

          {/* Matched items */}
          {matched.map((m, idx) => {
            const actions = getActionItems(m);
            const isNotBooked = m.current.status === 'NOT_BOOKED';
            const isReviewed = reviewed.has(m.current.id) || (m.current.remarks || '').includes('[Reviewed');
            const vendorName = m.prev.vendor_name || m.current.vendor_name;

            return (
              <div key={idx} className={`border rounded-xl overflow-hidden ${m.has_differences ? 'border-amber-300' : 'border-gray-200'}`}>
                {/* Item header */}
                <div className={`flex items-center justify-between px-3 py-2 text-xs ${m.has_differences ? 'bg-amber-50' : 'bg-green-50'}`}>
                  <div className="flex items-center gap-2">
                    {m.has_differences
                      ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                      : <CheckCircle className="w-3.5 h-3.5 text-green-600" />
                    }
                    <span className="font-semibold text-gray-800">{m.current.name}</span>
                    <span className="text-gray-400">Day {m.current.day_number}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {/* Mark reviewed */}
                    <button
                      onClick={() => markReviewed(m.current.id)}
                      disabled={reviewing.has(m.current.id) || isReviewed}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-colors ${
                        isReviewed
                          ? 'bg-green-100 text-green-700 cursor-default'
                          : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {reviewing.has(m.current.id)
                        ? <RefreshCw className="w-3 h-3 animate-spin" />
                        : <Check className="w-3 h-3" />
                      }
                      {isReviewed ? 'Reviewed' : 'Mark reviewed'}
                    </button>
                    {/* Copy from previous */}
                    {isNotBooked && m.prev.status !== 'NOT_BOOKED' && (
                      <button
                        onClick={() => carryForward(m.prev.id, m.current.id)}
                        disabled={carrying.has(m.current.id)}
                        className="flex items-center gap-1 px-2 py-1 bg-indigo-600 text-white rounded text-[10px] font-medium hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {carrying.has(m.current.id)
                          ? <RefreshCw className="w-3 h-3 animate-spin" />
                          : <ArrowRight className="w-3 h-3" />
                        }
                        Copy from previous
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-3 space-y-2.5">
                  {/* Side-by-side */}
                  <div className="grid grid-cols-2 gap-2">
                    <ItemDetail item={m.prev} side="prev" label={`Previous (${data.previous_version.wyn_id})`} />
                    <ItemDetail item={m.current} side="current" label={`Current (${data.current_version.wyn_id})`} />
                  </div>

                  {/* What needs to change */}
                  {actions.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                      <div className="text-[10px] font-semibold text-amber-700 mb-1.5 uppercase tracking-wide">What needs to change</div>
                      <ul className="space-y-0.5">
                        {actions.map((a, i) => (
                          <li key={i} className="flex items-start gap-1.5 text-xs text-amber-800">
                            <span className="mt-0.5 text-amber-500">•</span> {a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Vendor files */}
                  {vendorName && (
                    <VendorFilesRow
                      vendorName={vendorName}
                      prevItineraryId={data.previous_version.itinerary_id}
                      currentItineraryId={itineraryId}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Previous only — need cancellation */}
          {prev_only.length > 0 && (
            <div className="border border-red-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 text-xs font-semibold text-red-700">
                <XCircle className="w-3.5 h-3.5" />
                Booked in previous — NOT in current version ({prev_only.length}) · May need cancellation
              </div>
              <div className="p-3 space-y-2">
                {prev_only.map((item, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2 items-start">
                    <ItemDetail item={item} side="prev" label={`Previous (${data.previous_version.wyn_id})`} />
                    <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border border-dashed border-gray-300 text-xs text-gray-400 p-4">
                      Not in current version
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Current only — need booking */}
          {current_only.length > 0 && (
            <div className="border border-blue-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-xs font-semibold text-blue-700">
                <AlertTriangle className="w-3.5 h-3.5" />
                New in current version ({current_only.length}) · Need booking
              </div>
              <div className="p-3 space-y-2">
                {current_only.map((item, i) => (
                  <div key={i} className="grid grid-cols-2 gap-2 items-start">
                    <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border border-dashed border-gray-300 text-xs text-gray-400 p-4">
                      New item
                    </div>
                    <ItemDetail item={item} side="current" label={`Current (${data.current_version.wyn_id})`} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50 rounded-b-xl flex-shrink-0">
          <div className="text-xs">
            {summary.needs_attention > 0
              ? <span className="text-amber-700 font-medium">⚠ {summary.needs_attention} items need attention</span>
              : <span className="text-green-700 font-medium">✓ All items reviewed</span>
            }
          </div>
          <div className="flex gap-2">
            <button
              onClick={carryForwardAll}
              disabled={carryingAll}
              className="px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {carryingAll ? 'Carrying...' : 'Accept All NOT_BOOKED →'}
            </button>
            <button onClick={onClose} className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingReconciliationPanelV2;
